import React, { useState, useEffect } from 'react';
import { masterSupabase } from '../lib/supabase-master.js';

const VPIFrontendTest = () => {
  const [session, setSession] = useState(null);
  const [vpiData, setVpiData] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logs, setLogs] = useState([]);

  // Helper function to add logs
  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[VPI-TEST ${type.toUpperCase()}] ${message}`);
  };

  // Get current session
  useEffect(() => {
    const getSession = async () => {
      const { data: { session }, error } = await masterSupabase.auth.getSession();
      if (error) {
        addLog(`Session error: ${error.message}`, 'error');
      } else if (session) {
        setSession(session);
        addLog(`✅ Session found: ${session.user.email}`, 'success');
        addLog(`🔑 Token: ${session.access_token.substring(0, 20)}...`, 'info');
      } else {
        addLog('❌ No session found - user needs to login', 'error');
      }
    };
    getSession();
  }, []);

  // Test VPI API Status
  const testVPIStatus = async () => {
    if (!session) {
      addLog('❌ No session - cannot test VPI API', 'error');
      return;
    }

    setLoading(true);
    setError(null);
    addLog('🔄 Testing VPI API Status...', 'info');

    try {
      const response = await fetch('https://clara360.de/vpi-api/status', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'clara360-frontend-test'
        }
      });

      addLog(`📡 Request sent with Authorization header`, 'info');
      addLog(`📊 Response status: ${response.status}`, response.ok ? 'success' : 'error');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setVpiData(data);
      addLog(`✅ VPI Status received: ${JSON.stringify(data, null, 2)}`, 'success');
      
    } catch (err) {
      setError(err.message);
      addLog(`❌ VPI Status error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Test VPI Eligible Tenants (MAIN TEST)
  const testVPITenants = async () => {
    if (!session) {
      addLog('❌ No session - cannot test VPI Tenants', 'error');
      return;
    }

    setLoading(true);
    setError(null);
    addLog('🔄 Testing VPI Eligible Tenants...', 'info');

    try {
      const response = await fetch('https://clara360.de/vpi-api/eligible-tenants', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'clara360-frontend-test'
        }
      });

      addLog(`📡 Request sent to /vpi-api/eligible-tenants`, 'info');
      addLog(`📊 Response status: ${response.status}`, response.ok ? 'success' : 'error');

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      setTenants(data.tenants || []);
      
      addLog(`✅ Tenants received: ${data.count} tenants`, 'success');
      addLog(`👤 User: ${data.user}`, 'info');
      
      if (data.tenants && data.tenants.length > 0) {
        addLog(`📋 Sample tenant: ${JSON.stringify(data.tenants[0], null, 2)}`, 'success');
      } else {
        addLog('⚠️ No tenants found - check RLS or data', 'warning');
      }
      
    } catch (err) {
      setError(err.message);
      addLog(`❌ VPI Tenants error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Test VPI Calculation for first tenant
  const testVPICalculation = async () => {
    if (!session || tenants.length === 0) {
      addLog('❌ No session or tenants - cannot test calculation', 'error');
      return;
    }

    const firstTenant = tenants[0];
    setLoading(true);
    addLog(`🔄 Testing VPI Calculation for tenant: ${firstTenant.name}`, 'info');

    try {
      const response = await fetch(`https://clara360.de/vpi-api/tenants/${firstTenant.id}/calculation`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'X-Client-Info': 'clara360-frontend-test'
        }
      });

      addLog(`📡 Request sent to /vpi-api/tenants/${firstTenant.id}/calculation`, 'info');
      addLog(`📊 Response status: ${response.status}`, response.ok ? 'success' : 'error');

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const calculation = await response.json();
      addLog(`✅ VPI Calculation: ${JSON.stringify(calculation, null, 2)}`, 'success');
      
    } catch (err) {
      addLog(`❌ VPI Calculation error: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>🧪 VPI Frontend Test Suite</h2>
      
      {/* Session Status */}
      <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: session ? '#d4edda' : '#f8d7da', borderRadius: '5px' }}>
        <strong>Session Status:</strong> {session ? `✅ Logged in as ${session.user.email}` : '❌ Not logged in'}
      </div>

      {/* Test Buttons */}
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={testVPIStatus} 
          disabled={!session || loading}
          style={{ marginRight: '10px', padding: '10px 20px' }}
        >
          🔍 Test VPI Status
        </button>
        
        <button 
          onClick={testVPITenants} 
          disabled={!session || loading}
          style={{ marginRight: '10px', padding: '10px 20px' }}
        >
          👥 Test VPI Tenants (MAIN)
        </button>
        
        <button 
          onClick={testVPICalculation} 
          disabled={!session || loading || tenants.length === 0}
          style={{ padding: '10px 20px' }}
        >
          🧮 Test VPI Calculation
        </button>
      </div>

      {/* Loading */}
      {loading && <div style={{ color: 'blue' }}>⏳ Loading...</div>}

      {/* Error */}
      {error && (
        <div style={{ color: 'red', backgroundColor: '#f8d7da', padding: '10px', borderRadius: '5px', marginBottom: '20px' }}>
          ❌ Error: {error}
        </div>
      )}

      {/* VPI Data */}
      {vpiData && (
        <div style={{ marginBottom: '20px' }}>
          <h3>📊 VPI Status Data:</h3>
          <pre style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', overflow: 'auto' }}>
            {JSON.stringify(vpiData, null, 2)}
          </pre>
        </div>
      )}

      {/* Tenants Data */}
      {tenants.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <h3>👥 VPI Eligible Tenants ({tenants.length}):</h3>
          <pre style={{ backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px', overflow: 'auto', maxHeight: '300px' }}>
            {JSON.stringify(tenants, null, 2)}
          </pre>
        </div>
      )}

      {/* Logs */}
      <div>
        <h3>📝 Test Logs:</h3>
        <div style={{ backgroundColor: '#000', color: '#fff', padding: '10px', borderRadius: '5px', maxHeight: '400px', overflow: 'auto' }}>
          {logs.map((log, index) => (
            <div key={index} style={{ 
              color: log.type === 'error' ? '#ff6b6b' : 
                     log.type === 'success' ? '#51cf66' : 
                     log.type === 'warning' ? '#ffd43b' : '#fff',
              marginBottom: '5px'
            }}>
              [{log.timestamp}] {log.message}
            </div>
          ))}
        </div>
      </div>

      {/* Instructions */}
      <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
        <h4>🔹 Test Instructions:</h4>
        <ol>
          <li><strong>Login first</strong> - Make sure you're logged in to Clara360</li>
          <li><strong>Test VPI Status</strong> - Verify API is reachable with auth</li>
          <li><strong>Test VPI Tenants</strong> - Main test for RLS + user-specific data</li>
          <li><strong>Check Network Tab</strong> - Verify Authorization header is sent</li>
          <li><strong>Check Server Logs</strong> - PM2 logs should show auth success</li>
        </ol>
      </div>
    </div>
  );
};

export default VPIFrontendTest;

